import type { MediaFile } from '@connected/api-client/database/schema';
import type { UseMutationOptions, UseMutationResult } from '@tanstack/react-query';

import { client } from '@connected/api-client';
import { useMediaManager } from '@connected/api-client/hooks/use-media-manager';
import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { ElectricalProjectFile } from '@/hooks/use-electrical-project-files';

import { useElectricalProjectFiles } from '@/hooks/use-electrical-project-files';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useOffline } from '@/stores/network-status';

export type UseFileDeleteOptions = {
    mutationOptions?: Omit<UseMutationOptions<void, Error, string, Promise<FileDeleteMutationContext>>, 'mutationFn' | 'onMutate' | 'onSuccess' | 'onError'> & {
        onMutate?: (electricalProjectFileId: string) => Promise<void>;
        onSuccess?: (data: void, variables: string, context: FileDeleteMutationContext) => Promise<void>;
        onError?: (error: Error, variables: string, context: FileDeleteMutationContext) => Promise<void>;
    };
};

export type UseFileDeleteResult = {
    mutation: UseMutationResult<void, Error, string, Promise<FileDeleteMutationContext>>;
    isDeleting: boolean;
    isDisabled: boolean;
    delete: (electricalProjectFileId: string) => void;
};

export type FileDeleteMutationContext = {
    previousFiles: ElectricalProjectFile[];
    deletedFile: ElectricalProjectFile | undefined;
    mediaFile: MediaFile | null;
};

export async function handleDelete(electricalProjectFileId: string): Promise<void> {
    const response = await client.DELETE('/api/v2/electrical/projects/files/delete', {
        params: {
            query: {
                ElectricalProjectFileId: electricalProjectFileId,
            },
        },
    });

    if (response.error) {
        throw response.error;
    }
}

export function useElectricalProjectFileDelete({ mutationOptions }: UseFileDeleteOptions = {}): UseFileDeleteResult {
    const isUserOffline = useOffline();
    const canOfflineFiles = useFeatureFlag({ flag: 'offline-files' });

    const { context: { electricalProjectVersionId } } = useElectricalProject();
    const { generateMediaFileId, files } = useElectricalProjectFiles();
    const mediaManager = useMediaManager();
    const queryClient = useQueryClient();

    const projectFilesQueryOptions = elektraAppQueryOptions.project.files.listOptions({ electricalProjectVersionId });

    const mutation = useMutation<void, Error, string, Promise<FileDeleteMutationContext>>({
        ...mutationOptions,
        mutationKey: ['delete', '/api/v2/electrical/projects/files/delete'],
        mutationFn: async (electricalProjectFileId) => {
            return handleDelete(electricalProjectFileId);
        },
        onMutate: async (electricalProjectFileId) => {
            await queryClient.cancelQueries(projectFilesQueryOptions);

            const fileToDelete = files?.find(f => f.ElectricalProjectFileId === electricalProjectFileId);

            // If file is already deleted (not found in cache), return early without error
            // This can happen if the UI hasn't updated yet after a previous successful deletion
            if (!fileToDelete) {
                // Return a context that indicates the file was already deleted
                return {
                    previousFiles: files,
                    deletedFile: undefined,
                    mediaFile: null,
                };
            }

            // Remove the file from the local cache optimistically
            const newFiles = files.filter(f => f.ElectricalProjectFileId !== electricalProjectFileId);

            // Get the media file for cleanup
            const mediaFileId = generateMediaFileId(fileToDelete);
            const mediaFile = await mediaManager.getMediaFile(mediaFileId);

            queryClient.setQueryData(projectFilesQueryOptions.queryKey, {
                Files: newFiles,
                Count: newFiles.length,
            });

            return {
                previousFiles: files,
                deletedFile: fileToDelete,
                mediaFile: mediaFile ?? null,
            };
        },
        onSuccess: async (data, variables, context) => {
            const contextData = await context;

            // Only clean up media file if we actually deleted a file
            if (contextData.deletedFile && contextData.mediaFile) {
                await mediaManager.remove(contextData.mediaFile);
            }

            // Invalidate the query to ensure we have the latest data
            queryClient.invalidateQueries(projectFilesQueryOptions);

            if (mutationOptions?.onSuccess) {
                await mutationOptions.onSuccess(data, variables, contextData);
            }
        },
        onError: async (error, variables, context) => {
            const contextData = await context;

            // Restore the previous files on error
            if (contextData?.previousFiles) {
                queryClient.setQueryData(projectFilesQueryOptions.queryKey, {
                    Files: contextData.previousFiles,
                    Count: contextData.previousFiles.length,
                });
            }

            if (mutationOptions?.onError) {
                await mutationOptions.onError(error, variables, contextData!);
            }

            console.error('Error deleting file:', error);
        },
    });

    const deleteFile = (electricalProjectFileId: string) => {
        mutation.mutate(electricalProjectFileId);
    };

    const isDisabled = useMemo(() => {
        return (isUserOffline && !canOfflineFiles) || mutation.isPending;
    }, [isUserOffline, canOfflineFiles, mutation.isPending]);

    return {
        mutation,
        isDeleting: mutation.isPending,
        isDisabled,
        delete: deleteFile,
    };
}
